if (!customElements.get("product-variant-preview")) {
  customElements.define(
    "product-variant-preview",

    class ProductVariantPreview extends HTMLElement {
      static options = {
        cardMediaClass: "card--media",
        isEmptyClass: "is--empty",
        itemSelector: ".product-variant-preview--item",
        mediaActiveClass: "is--active",
        mediaPreviousClass: "is--previous",
        mediaSelector: ".product-variant-preview-media",
        productCardLinkSelector: 'a[href][id^="CardLink"]',
        productCardSelector: ".card",
      };

      constructor() {
        super();
        this.options = ProductVariantPreview.options;

        document.addEventListener("DOMContentLoaded", this.init.bind(this));
      }

      /**
       * Initialize the product variant preview
       */
      init() {
        this.elements = {
          /**
           * @type {?HTMLElement}
           */
          productCard: null,

          /**
           * @type {?HTMLElement}
           */
          productCardLink: null,

          /**
           * @type {Array<HTMLElement>}
           */
          items: [],

          /**
           * @type {Array<HTMLElement>}
           */
          media: [],
        };

        this.states = {
          /**
           * @type {?string}
           */
          productCardLinkUrl: null,
        };

        this.onTransitionEnd = this.onTransitionEnd.bind(this);

        this.collectElements();

        if (this.shouldContinueInit() === false) {
          return;
        }

        this.initStates();
        this.initEvents();
      }

      /**
       * Collect necessary elements
       */
      collectElements() {
        this.elements.productCard = this.closest(
          this.options.productCardSelector
        );
        if (this.elements.productCard === null) {
          return;
        }

        this.elements.productCardLink = this.elements.productCard.querySelector(
          this.options.productCardLinkSelector
        );

        this.elements.items = Array.from(
          this.querySelectorAll(this.options.itemSelector)
        );

        this.elements.media = Array.from(
          this.elements.productCard.querySelectorAll(this.options.mediaSelector)
        );
      }

      /**
       * Init events
       */
      initEvents() {
        this.elements.items.forEach((item) => {
          item.addEventListener(
            "mouseenter",
            this.onItemInteraction.bind(this)
          );
          item.addEventListener(
            "touchstart",
            this.onItemInteraction.bind(this)
          );

          if ("ontouchstart" in window) {
            item.addEventListener("click", this.onItemInteraction.bind(this));
          }
        });

        this.addEventListener("mouseleave", this.onMouseLeave.bind(this));
      }

      /**
       * Init states
       */
      initStates() {
        if (this.elements.productCardLink !== null) {
          this.states.productCardLinkUrl =
            this.elements.productCardLink.getAttribute("href");
        }
      }

      /**
       * Handle media change in three steps to prevent flickering:
       * * Mark current active media as previous and inactive
       * * Mark target media as active
       * * After transition remove any previous state
       * @param {string} variantId
       */
      handleMediaChange(variantId) {
        const targetMedia =
          this.elements.media.find((media) => {
            return media.dataset.variantId === variantId;
          }) || null;

        const currentMedia =
          this.elements.media.find((media) => {
            return media.classList.contains(this.options.mediaActiveClass);
          }) || null;

        if (targetMedia === null || currentMedia === targetMedia) {
          return;
        }

        if (currentMedia !== null) {
          currentMedia.classList.add(this.options.mediaPreviousClass);
          currentMedia.classList.remove(this.options.mediaActiveClass);
        }

        targetMedia.classList.add(this.options.mediaActiveClass);
        targetMedia.addEventListener("transitionend", this.onTransitionEnd);
      }

      /**
       * Update card link to new url
       * @param {string} url
       */
      handleUrlChange(url) {
        this.elements.productCardLink.setAttribute("href", url);
      }

      /**
       * Don't continue init if it's empty and parent card not found or
       * has no media
       * @return {boolean}
       */
      shouldContinueInit() {
        return (
          (this.classList.contains(this.options.isEmptyClass) === true ||
            this.elements.productCard === null ||
            this.elements.productCard.classList.contains(
              this.options.cardMediaClass
            ) === false) === false
        );
      }

      /**
       * Handle item interaction on hover & touch
       * @param {Event} event
       * @return {boolean}
       */
      onItemInteraction(event) {
        const target = event.currentTarget;
        const variantId = target.dataset.variantId;
        const url = target.getAttribute("href");

        if (variantId === undefined) {
          return true;
        }

        event.preventDefault();

        this.handleMediaChange(variantId);
        this.handleUrlChange(url);
      }

      /**
       * On leaving the element, reset any changes
       */
      onMouseLeave() {
        this.elements.media.forEach((media) => {
          media.classList.remove(this.options.mediaActiveClass);
          media.classList.remove(this.options.mediaPreviousClass);
        });

        this.elements.productCardLink.setAttribute(
          "href",
          this.states.productCardLinkUrl
        );
      }

      /**
       * On transition end, remove previous state, then clean up itself
       * @param {Event} event
       */
      onTransitionEnd(event) {
        this.elements.media.forEach((media) => {
          media.classList.remove(this.options.mediaPreviousClass);
        });

        event.target.removeEventListener("transitionend", this.onTransitionEnd);
      }
    }
  );
}
